import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../lib/apiClient';
import { useAuth } from '../../lib/auth/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { MonitoringUsageResponse, MonitoredUrl, MonitoringHistory } from '../../types';
import {
    Button,
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
    Input,
    Label,
    Slider,
    Skeleton,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@pagespeedify/ui';
import { Bell } from 'lucide-react';
import { toast } from 'sonner';
import { validateThresholds, validateThresholdsForCurrentScoreBaseline, idealScores } from '../../utils/validation';
import { useDeleteQueue } from '../tasks/DeleteQueueContext';
import { getMetricsForPlan, metricInfo, getUserPlanType, calculateIdealScoreRange, calculateCurrentScoreRange } from '../../utils/monitoring';
import { Task } from '../../types';

// --- Helper to determine metric status ---
const getMetricStatus = (metric: string, value: number | null) => {
    if (value === null) return { color: 'text-muted-foreground' };
    const thresholds = {
        performance_score: { good: 90, poor: 50 }, // Higher is better
        lcp: { good: 2500, poor: 4000 },
        cls: { good: 0.1, poor: 0.25 },
        fcp: { good: 1800, poor: 3000 },
        ttfb: { good: 800, poor: 1800 },
        tbt: { good: 200, poor: 600 },
        tti: { good: 3800, poor: 7300 },
    };
    const metricKey = metric.toLowerCase() as keyof typeof thresholds;
    if (!thresholds[metricKey]) return { color: 'text-muted-foreground' };

    const { good, poor } = thresholds[metricKey];

    if (metric === 'performance_score') {
        if (value >= good) return { color: 'text-report-good' };
        if (value < poor) return { color: 'text-report-fail' };
        return { color: 'text-report-improvement' };
    } else {
        if (value <= good) return { color: 'text-report-good' };
        if (value > poor) return { color: 'text-report-fail' };
        return { color: 'text-report-improvement' };
    }
};

// --- Helper to format metric values the same way as TaskModal ---
const formatMetricValue = (key: string, value: any): string => {
    if (value === null || value === undefined) return '—';

    const numValue = typeof value === 'string' ? parseFloat(value) : value;

    switch (key.toLowerCase()) {
        case 'cls':
            return numValue.toFixed(3);
        case 'fcp':
        case 'lcp':
        case 'tti':
            return `${(numValue / 1000).toFixed(2)}s`;
        case 'tbt':
        case 'inp':
        case 'ttfb':
            return `${numValue.toFixed(2)}ms`;
        default:
            return String(value);
    }
};

interface EditMonitoringTaskFormProps {
    monitoredUrl: MonitoredUrl;
    task: Task;
    onClose: () => void;
}

interface UpdatePayload {
    is_active: boolean;
    title: string;
    description: string;
    ideal_thresholds?: {
        lcp?: number;
        cls?: number;
        tti?: number;
        fcp?: number;
        ttfb?: number;
        tbt?: number;
    };
    current_thresholds?: {
        lcp?: number;
        cls?: number;
        tti?: number;
        fcp?: number;
        ttfb?: number;
        tbt?: number;
    };
    threshold_baseline?: 'ideal' | 'current';
    status?: 'open' | 'in_progress' | 'completed' | 'closed';
    priority?: 'low' | 'medium' | 'high';
}

export const EditMonitoringTaskForm: React.FC<EditMonitoringTaskFormProps> = ({ monitoredUrl, task, onClose }) => {
    const { user, isLoading: authIsLoading } = useAuth();
    const apiClient = useApi();
    const queryClient = useQueryClient();
    const { addTaskToDeleteQueue } = useDeleteQueue();

    const { data: monitoringUsage, isLoading: isUsageLoading } = useQuery<MonitoringUsageResponse, Error>({
        queryKey: ['monitoringUsage', user?.uid],
        queryFn: async () => {
            if (!apiClient) throw new Error('API client not available');
            const response = await apiClient.get<{ success: boolean, usage: MonitoringUsageResponse }>('/monitoring/usage');
            if (!response.success || !response.usage) throw new Error('Failed to fetch monitoring usage data.');
            return response.usage;
        },
        enabled: !!user && !authIsLoading && !!apiClient,
    });
    const { data: history, isLoading: isHistoryLoading } = useQuery<MonitoringHistory[], Error>({
        queryKey: ['monitoringHistory', monitoredUrl.id],
        queryFn: async () => {
            if (!apiClient) return [];
            const response = await apiClient.get<{ success: boolean, data: MonitoringHistory[] }>(`/monitoring/history/${monitoredUrl.id}`);
            if (!response.success || !response.data) {
                throw new Error('Failed to fetch monitoring history.');
            }
            return response.data;
        },
        enabled: !!user && !!apiClient,
    });

    const latestScan = history && history.length > 0 ? history[0] : null;
    const [isActive, setIsActive] = useState(monitoredUrl.is_active ?? true);
    const [title, setTitle] = useState(task.title || '');
    const [description, setDescription] = useState(task.description || '');
    const normalizePriority = (p: string | number | undefined) => {
        if (typeof p === 'number') {
            return p === 1 ? 'low' : p === 2 ? 'medium' : 'high';
        }
        if (typeof p === 'string') return p;
        return 'medium';
    };

    const [status, setStatus] = useState<string>(task.status || 'open');
    const [priority, setPriority] = useState<string>(normalizePriority(task.priority));
    // Initialize baseline from saved data, default to 'ideal'
    const savedBaseline = ((monitoredUrl.additional_metrics as any)?.threshold_baseline || 'ideal') as 'ideal' | 'current';
    const [thresholds, setThresholds] = useState({ ...idealScores });
    const [thresholdBaseline, setThresholdBaseline] = useState<'ideal' | 'current'>(savedBaseline);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Store both threshold sets separately
    const [idealThresholds, setIdealThresholds] = useState({ ...idealScores });
    const [currentThresholds, setCurrentThresholds] = useState({ ...idealScores });

    // Load thresholds from the task's saved alerts when monitoredUrl changes
    useEffect(() => {
        const additionalMetrics = (monitoredUrl.additional_metrics as any) || {};
        const savedBaseline = additionalMetrics.threshold_baseline || 'ideal';

        // Load BOTH threshold sets
        let idealRawAlerts = additionalMetrics.ideal_thresholds || additionalMetrics.alerts || {};
        let currentRawAlerts = additionalMetrics.current_thresholds || {};

        // Normalize ideal alerts
        const normalizedIdealAlerts: Record<string, number> = {};
        Object.keys(idealRawAlerts || {}).forEach((k) => {
            const v = idealRawAlerts[k];
            if (v && typeof v === 'object' && v.threshold !== undefined) {
                normalizedIdealAlerts[k] = Number(v.threshold);
            } else if (v !== undefined && v !== null) {
                normalizedIdealAlerts[k] = Number(v);
            }
        });

        // Normalize current alerts
        const normalizedCurrentAlerts: Record<string, number> = {};
        Object.keys(currentRawAlerts || {}).forEach((k) => {
            const v = currentRawAlerts[k];
            if (v && typeof v === 'object' && v.threshold !== undefined) {
                normalizedCurrentAlerts[k] = Number(v.threshold);
            } else if (v !== undefined && v !== null) {
                normalizedCurrentAlerts[k] = Number(v);
            }
        });

        // Set both threshold states
        const finalIdealThresholds = Object.keys(normalizedIdealAlerts).length > 0
            ? { ...idealScores, ...normalizedIdealAlerts }
            : { ...idealScores };
        const finalCurrentThresholds = Object.keys(normalizedCurrentAlerts).length > 0
            ? { ...idealScores, ...normalizedCurrentAlerts }
            : { ...idealScores };

        setIdealThresholds(finalIdealThresholds as any);
        setCurrentThresholds(finalCurrentThresholds as any);

        // Set active thresholds based on saved baseline
        setThresholds(savedBaseline === 'current' ? finalCurrentThresholds : finalIdealThresholds);
        setThresholdBaseline(savedBaseline);
    }, [monitoredUrl.id, monitoredUrl.additional_metrics]);

    const getMetricsToShow = React.useCallback(() => {
        const planFeatures = monitoringUsage?.accessContext?.planFeatures;
        const planType = getUserPlanType(planFeatures, user?.user_type);
        return getMetricsForPlan(planType);
    }, [monitoringUsage, user]);

    const handleThresholdChange = React.useCallback((metric: string, value: string | number[]) => {
        const info = metricInfo[metric as keyof typeof metricInfo];
        let numericValue = Array.isArray(value) ? value[0] : Number(value);

        // CLS cannot be negative (min = 0)
        if (metric === 'cls' && numericValue < 0) {
            numericValue = 0;
        }

        const storedValue = numericValue * info.conversion;

        // Update active thresholds
        setThresholds((prev: any) => ({ ...prev, [metric]: storedValue } as any));

        // Also update the appropriate threshold set based on current baseline
        if (thresholdBaseline === 'current') {
            setCurrentThresholds((prev: any) => ({ ...prev, [metric]: storedValue } as any));
        } else {
            setIdealThresholds((prev: any) => ({ ...prev, [metric]: storedValue } as any));
        }
    }, [thresholdBaseline]);

    const mutation = useMutation({
        mutationFn: (payload: UpdatePayload) => apiClient.patch(`/monitoring/${monitoredUrl.id}`, payload),
        onSuccess: () => {
            toast.success('Monitoring task updated successfully!');
            queryClient.invalidateQueries({ queryKey: ['monitoredUrls'] });
            queryClient.invalidateQueries({ queryKey: ['ai-tasks-list'] });
            // Refresh credits - billing happens async so add slight delay
            setTimeout(() => {
                queryClient.invalidateQueries({ queryKey: ['monitoringUsage'] });
                queryClient.invalidateQueries({ queryKey: ['user'] });
            }, 1500);
            onClose();
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || 'Failed to update monitoring task.');
        },
    });

    const acknowledgeMutation = useMutation({
        mutationFn: () => apiClient.post(`/monitoring/${monitoredUrl.id}/acknowledge`, {}),
        onSuccess: () => {
            toast.success('Breach acknowledged! Notification counter reset.');
            queryClient.invalidateQueries({ queryKey: ['monitoredUrls'] });
            queryClient.invalidateQueries({ queryKey: ['ai-tasks-list'] });
            queryClient.invalidateQueries({ queryKey: ['monitoringHistory', monitoredUrl.id] });
        },
        onError: (error: any) => {
            toast.error(error?.response?.data?.message || 'Failed to acknowledge breach.');
        },
    });

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        // Choose validation based on which baseline is selected
        if (thresholdBaseline === 'current') {
            // For current scores baseline, use smart range validation
            const currentScores: Record<string, number | null> = {};
            getMetricsToShow().forEach(metric => {
                currentScores[metric] = latestScan ? (latestScan as any)[metric] ?? null : null;
            });
            if (!validateThresholdsForCurrentScoreBaseline(thresholds, currentScores, getMetricsToShow())) {
                return;
            }
        } else {
            // For ideal baseline, use standard validation
            if (!validateThresholds(thresholds, getMetricsToShow())) {
                return;
            }
        }

        const maybeStatus = status as string;
        const maybePriority = priority as string;
        const mapStatusForBackend = (s: string | undefined) => {
            if (!s) return undefined;
            return s;
        };
        const mapPriorityForBackend = (p: string | undefined) => {
            if (!p) return undefined;
            return p;
        };

        mutation.mutate({
            is_active: isActive,
            title,
            description,
            ideal_thresholds: idealThresholds,
            current_thresholds: currentThresholds,
            threshold_baseline: thresholdBaseline,
            status: mapStatusForBackend(maybeStatus) as any,
            priority: mapPriorityForBackend(maybePriority) as any
        });
    };

    const handleDelete = () => {
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        addTaskToDeleteQueue(task);
        onClose();
    };

    // Calculate estimated refund for overage URL
    const getEstimatedRefund = () => {
        if (!monitoringUsage) return null;
        const overageCost = monitoringUsage.overageCostPerUnit || 0;
        const consumedUrls = monitoringUsage.consumedInPeriod || 0;
        const limitUrls = monitoringUsage.limitForPeriod || 0;
        const isOverageUrl = consumedUrls > limitUrls;

        if (!isOverageUrl || overageCost <= 0) return null;

        const periodEnds = monitoringUsage.accessContext?.planFeatures?.current_period_ends;
        if (!periodEnds) return { estimated: true, credits: 0 };

        const now = new Date();
        const endDate = new Date(periodEnds);
        const daysRemaining = Math.max(0, Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
        const totalDays = 30;
        const proRataRefund = Math.ceil((daysRemaining / totalDays) * overageCost);

        return { estimated: true, credits: proRataRefund, daysRemaining };
    };

    if (isUsageLoading || isHistoryLoading) {
        return <Skeleton className="h-48 w-full" />;
    }

    return (
        <>
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <CardTitle>Edit Monitoring Task</CardTitle>
                        <div className="flex flex-col items-end gap-2">
                            <div className="flex items-center gap-3">
                                <Label htmlFor="pause-toggle" className={`text-sm ${isActive ? 'text-green-600' : 'text-muted-foreground'}`}>
                                    {isActive ? 'Active' : 'Paused'}
                                </Label>
                                <Switch
                                    id="pause-toggle"
                                    checked={isActive}
                                    onCheckedChange={(checked) => {
                                        // If trying to resume (paused -> active)
                                        if (!isActive && checked) {
                                            // Check if user has enough credits for pro-rata charge
                                            const overageCost = monitoringUsage?.overageCostPerUnit || 0;
                                            const consumedUrls = monitoringUsage?.consumedInPeriod || 0;
                                            const limitUrls = monitoringUsage?.limitForPeriod || 0;
                                            const isOverageUrl = consumedUrls > limitUrls;

                                            if (isOverageUrl && overageCost > 0) {
                                                const userCredits = user?.credits || 0;
                                                if (userCredits < overageCost) {
                                                    toast.error(`Insufficient credits to resume. Required: ${overageCost}, Available: ${userCredits}`);
                                                    return; // Don't allow toggle
                                                }
                                            }
                                        }
                                        setIsActive(checked);
                                    }}
                                />
                            </div>
                            {/* Show warning if trying to resume with insufficient credits */}
                            {!monitoredUrl.is_active && isActive && (() => {
                                const consumedUrls = monitoringUsage?.consumedInPeriod || 0;
                                const limitUrls = monitoringUsage?.limitForPeriod || 0;
                                const overageCost = monitoringUsage?.overageCostPerUnit || 0;
                                const userCredits = monitoringUsage?.canCreate?.userCreditBalance ?? user?.credits ?? 0;
                                const wouldNeedOverage = consumedUrls >= limitUrls;

                                if (wouldNeedOverage && overageCost > 0 && userCredits < overageCost) {
                                    return (
                                        <div className="mt-2 p-3 bg-red-900/20 border border-red-500/30 rounded-md">
                                            <p className="text-sm text-red-400 font-medium">
                                                Insufficient credits to reactivate
                                            </p>
                                            <p className="text-xs text-red-400/80 mt-1">
                                                You need <span className="font-semibold">{overageCost.toLocaleString()}</span> credits but only have <span className="font-semibold">{userCredits.toLocaleString()}</span>.
                                            </p>
                                            <a
                                                href="/credits"
                                                className="text-xs text-blue-400 hover:text-blue-300 underline mt-2 inline-block"
                                            >
                                                Add credits to continue →
                                            </a>
                                        </div>
                                    );
                                }
                                return null;
                            })()}
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="title">Title</Label>
                            <Input id="title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter a title for the monitoring task" />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="description">Description</Label>
                            <Input id="description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Enter a description (optional)" />
                        </div>
                        <div>
                            <Label htmlFor="website-url">Website URL</Label>
                            <Input id="website-url" value={monitoredUrl.website_url || ''} readOnly disabled />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="status">Task Status</Label>
                                <Select value={status} onValueChange={(value) => setStatus(value as any)}>
                                    <SelectTrigger id="status" className="w-full mt-1">
                                        <SelectValue placeholder="Select status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="open">Open</SelectItem>
                                        <SelectItem value="in_progress">In Progress</SelectItem>
                                        <SelectItem value="resolved">Resolved</SelectItem>
                                        <SelectItem value="closed">Closed</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="priority">Task Priority</Label>
                                <Select value={priority} onValueChange={(value) => setPriority(value as any)}>
                                    <SelectTrigger id="priority" className="w-full mt-1">
                                        <SelectValue placeholder="Select priority" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="low">Low</SelectItem>
                                        <SelectItem value="medium">Medium</SelectItem>
                                        <SelectItem value="high">High</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {monitoredUrl.has_reached_notification_limit && (
                            <div className="rounded-lg bg-muted p-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="font-medium">Acknowledge Breach</h4>
                                        <p className="text-sm text-muted-foreground">Reset notification counter without changing task status</p>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="icon"
                                        onClick={() => acknowledgeMutation.mutate()}
                                        disabled={acknowledgeMutation.isPending}
                                    >
                                        <Bell className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}

                        <div>
                            <h4 className="text-lg font-medium">Alert Thresholds</h4>
                            <>
                                <CardDescription>Set the values that will trigger an alert. (15% deviation from selected baseline is allowed)</CardDescription>

                                {/* Threshold Baseline Toggle - Only show if we have current scores */}
                                {latestScan !== null && (
                                    <div className="my-4 space-y-3">
                                        <div className="flex items-center gap-4 rounded-lg bg-muted p-3">
                                            <span className="text-sm font-medium">Threshold Baseline:</span>
                                            <div className="flex gap-4">
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="baseline"
                                                        value="ideal"
                                                        checked={thresholdBaseline === 'ideal'}
                                                        onChange={(e) => {
                                                            const newBaseline = e.target.value as 'ideal' | 'current';
                                                            setThresholdBaseline(newBaseline);

                                                            // Load saved ideal thresholds when switching to ideal baseline
                                                            if (newBaseline === 'ideal') {
                                                                setThresholds(idealThresholds);
                                                            }
                                                        }}
                                                        className="cursor-pointer"
                                                    />
                                                    <span className="text-sm">Ideal Scores</span>
                                                </label>
                                                <label className="flex items-center gap-2 cursor-pointer">
                                                    <input
                                                        type="radio"
                                                        name="baseline"
                                                        value="current"
                                                        checked={thresholdBaseline === 'current'}
                                                        onChange={(e) => {
                                                            const newBaseline = e.target.value as 'ideal' | 'current';
                                                            setThresholdBaseline(newBaseline);

                                                            // Load saved current thresholds when switching to current baseline
                                                            if (newBaseline === 'current') {
                                                                setThresholds(currentThresholds);
                                                            }
                                                        }}
                                                        className="cursor-pointer"
                                                    />
                                                    <span className="text-sm">Current Scores</span>
                                                </label>
                                            </div>
                                        </div>
                                        {thresholdBaseline === 'current' && (
                                            <div className="rounded-lg bg-amber-50 dark:bg-amber-950 p-3 border border-amber-200 dark:border-amber-800">
                                                <p className="text-xs text-amber-900 dark:text-amber-200">
                                                    💡 <strong>Current Score Range:</strong> Thresholds are limited to ±15% of current scores. This helps catch meaningful performance regressions while avoiding false alerts. If no current data is available, falls back to ideal ±15%.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <Card className="pt-4">
                                    <CardContent className="space-y-4">
                                        {getMetricsToShow().map(metric => (
                                            <MetricRow
                                                key={metric}
                                                metric={metric}
                                                thresholds={thresholds}
                                                latestScan={latestScan}
                                                thresholdBaseline={thresholdBaseline}
                                                onThresholdChange={handleThresholdChange}
                                            />
                                        ))}
                                    </CardContent>
                                </Card>
                            </>
                        </div>

                        <div className="flex justify-between items-center">
                            <Button type="button" variant="destructive" onClick={handleDelete}>
                                Delete
                            </Button>
                            <div className="flex gap-2">
                                <Button type="button" variant="ghost" onClick={onClose} disabled={mutation.isPending}>
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={
                                        mutation.isPending ||
                                        // Disable if resuming (was paused, now active) without enough credits for overage
                                        (!monitoredUrl.is_active && isActive && (() => {
                                            const consumedUrls = monitoringUsage?.consumedInPeriod || 0;
                                            const limitUrls = monitoringUsage?.limitForPeriod || 0;
                                            const overageCost = monitoringUsage?.overageCostPerUnit || 0;
                                            const userCredits = monitoringUsage?.canCreate?.userCreditBalance ?? user?.credits ?? 0;

                                            // If consumed >= limit, reactivating this URL would need overage credits
                                            // (This URL was likely paused because it's an overage URL with no credits)
                                            const wouldNeedOverage = consumedUrls >= limitUrls;
                                            const hasInsufficientCredits = overageCost > 0 && userCredits < overageCost;

                                            return wouldNeedOverage && hasInsufficientCredits;
                                        })())
                                    }
                                >
                                    {mutation.isPending ? 'Saving...' : 'Save'}
                                </Button>
                            </div>
                        </div>
                    </form>
                </CardContent>
            </Card>

            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Monitoring URL?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {(() => {
                                const refund = getEstimatedRefund();
                                if (refund && refund.credits > 0) {
                                    return (
                                        <>
                                            This will permanently delete this monitoring URL.
                                            <br /><br />
                                            <strong>Estimated refund: ~{refund.credits} credits</strong>
                                            <br />
                                            <span className="text-sm text-muted-foreground">
                                                ({refund.daysRemaining} days remaining in billing period)
                                            </span>
                                        </>
                                    );
                                }
                                return 'This will permanently delete this monitoring URL. This action cannot be undone.';
                            })()}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDeleteConfirm}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
};

// Extracted and memoized row component for performance
const MetricRow = React.memo(({
    metric,
    thresholds,
    latestScan,
    thresholdBaseline,
    onThresholdChange
}: {
    metric: string;
    thresholds: any;
    latestScan: any;
    thresholdBaseline: 'ideal' | 'current';
    onThresholdChange: (metric: string, value: string | number[]) => void;
}) => {
    const info = metricInfo[metric as keyof typeof metricInfo];
    if (!info) return null;
    const idealScore = idealScores[metric as keyof typeof idealScores];
    if (!idealScore) return null;

    let thresholdValue = thresholds[metric as keyof typeof thresholds];
    if (typeof thresholdValue !== 'number' || isNaN(thresholdValue)) thresholdValue = idealScore;
    const displayThreshold = thresholdValue / info.conversion;

    // Get current scan value
    const rawVal = latestScan ? (latestScan as any)[metric] : null;
    const latestScore = rawVal !== null && rawVal !== undefined && !isNaN(Number(rawVal))
        ? Number(rawVal)
        : null;

    // Calculate range
    const { min, max } = thresholdBaseline === 'current'
        ? calculateCurrentScoreRange(latestScore, idealScore, info.conversion, metric)
        : calculateIdealScoreRange(idealScore, info.conversion);

    // Baseline score calculation
    let baselineScore: number;
    if (thresholdBaseline === 'current' && latestScore !== null && latestScore !== 0) {
        baselineScore = latestScore;
    } else {
        baselineScore = idealScore;
    }
    const baselineDisplayScore = baselineScore / info.conversion;

    // Deviation calculation
    const deviation = ((thresholdValue - baselineScore) / baselineScore) * 100;

    const handleSliderChange = React.useCallback((val: number[]) => {
        onThresholdChange(metric, val);
    }, [metric, onThresholdChange]);

    const handleInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        onThresholdChange(metric, e.target.value);
    }, [metric, onThresholdChange]);

    return (
        <div className="flex items-center space-x-4">
            <Label htmlFor={`${metric}-threshold`} className="w-24 font-medium">
                {metric.toUpperCase()} {info.unit && <span className="text-xs text-muted-foreground">({info.unit})</span>}
            </Label>
            <Slider
                id={`${metric}-threshold`}
                min={min}
                max={max}
                step={0.001}
                value={[displayThreshold]}
                onValueChange={handleSliderChange}
                className="flex-1"
            />
            <Input
                type="number"
                step="0.001"
                value={metric === 'cls' ? displayThreshold.toFixed(3) : displayThreshold.toFixed(2)}
                onChange={handleInputChange}
                className="w-24"
                min={metric === 'cls' ? min.toFixed(3) : min.toFixed(2)}
                max={metric === 'cls' ? max.toFixed(3) : max.toFixed(2)}
            />
            <div className="w-40 text-center text-sm">
                <p className={Math.abs(deviation) > 15 ? 'text-report-fail' : 'text-report-good'}>{deviation.toFixed(0)}%</p>
                {latestScore !== null ? (
                    <p className={`text-xs ${getMetricStatus(metric, latestScore).color}`}>
                        Current: {formatMetricValue(metric, latestScore)}
                    </p>
                ) : (
                    <p className="text-xs text-muted-foreground">Current: —</p>
                )}
                <p className="text-xs text-muted-foreground">
                    {thresholdBaseline === 'current' ? 'Base' : 'Ideal'}: {metric === 'cls' ? baselineDisplayScore.toFixed(3) : baselineDisplayScore.toFixed(2)}
                </p>
                {thresholdBaseline === 'current' && (
                    <p className="text-xs text-muted-foreground mt-1">
                        Range: {metric === 'cls' ? min.toFixed(3) : min.toFixed(2)}–{metric === 'cls' ? max.toFixed(3) : max.toFixed(2)}
                    </p>
                )}
            </div>
        </div>
    );
});
